import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"

import { useBooleanState } from "shared/lib/useBooleanState"
import { useCommonProperty } from "shared/lib/useCommonProperties"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { Action } from "shared/types/Action"
import { FormRef } from "shared/types/ReactHookForm"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  UseProductPositionMultiActionsArgs,
  useProductPositionMultiActions
} from "entities/product-positions/model/useProductPositionMultiActions"
import {
  MultiEditProductPositionFormData,
  MultiEditProductPositionFormProps
} from "widgets/multi-edit-product-position-form/MultiEditProductPositionForm"

import { ProductPositionMultiSelectSidebarProps } from "../ProductPositionMultiSelectSidebar"
import { ProductPositionMultiSelectSidebarViewModel } from "../ProductPositionMultiSelectSidebarView"

const FORM_ID = "multi-edit-product-position-sidebar-form"

export function useProductPositionMultiSelectSidebarViewModel({
  productPositions,
  callbacks,
  formOptions,
  canEdit = true,
  isDisabled,
  onSubmit,
  ...rest
}: ProductPositionMultiSelectSidebarProps): ProductPositionMultiSelectSidebarViewModel {
  const { t } = useTranslation()

  const {
    value: isEditing,
    setTrue: onEdit,
    setFalse: onCancel
  } = useBooleanState(false)

  const formRef: FormRef<MultiEditProductPositionFormData> = useRef(null)

  const { quantity } = useCommonProperty({
    objects: productPositions,
    key: "quantity"
  })

  useEffect(() => {
    formRef.current?.reset(
      { productPosition: { quantity } },
      { keepDirty: true, keepDirtyValues: true }
    )
  }, [quantity])

  const { handleSubmit, isSubmitting } =
    useFormSubmit<MultiEditProductPositionFormData>({
      onSubmit,
      onSuccess: onCancel
    })

  const formProps: MultiEditProductPositionFormProps = useMemo(
    () => ({
      id: FORM_ID,
      defaultFormData: { productPosition: { quantity } },
      options: formOptions,
      isReadonly: !isEditing,
      formRef,
      onSubmit: handleSubmit
    }),
    [quantity, isEditing, formRef, formOptions, handleSubmit]
  )

  const actionsArgs: UseProductPositionMultiActionsArgs = {
    ...callbacks,
    onEdit: canEdit ? onEdit : undefined,
    isCopyDisabled: isDisabled,
    isEditDisabled: isDisabled,
    isDeleteDisabled: isDisabled,
    isDuplicateDisabled: isDisabled
  }

  const { actions: normalActions } = useProductPositionMultiActions(actionsArgs)

  const editingActions = useMemo<Action[]>(
    () => [
      {
        id: "product-position-multi-select__edit__cancel",
        "data-testid": "product-position-multi-select__edit__cancel",
        onClick: onCancel,
        icon: IconName.CLOSE_CIRCLE,
        label: t("General.Cancel")
      },
      {
        id: "product-position-multi-select__edit__submit",
        "data-testid": "product-position-multi-select__edit__submit",
        type: "submit",
        form: FORM_ID,
        icon: IconName.CHECK_CIRCLE,
        label: t("General.Confirm"),
        disabled: isSubmitting
      }
    ],
    [onCancel, t, isSubmitting]
  )

  return {
    ...rest,
    formProps,
    actions: isEditing ? editingActions : normalActions,
    selectedChildCount: productPositions.length
  }
}
